/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 * Cup Customizer Mockup Service
 * Created: 2026-01-09
 *
 * SERVICE RIÊNG CHO CupCustomizerPage:
 * - User-controlled position and scale
 * - Real-time preview with InteractivePreview
 * - Color transformation (laser/silk)
 * - Number of logos = Number of red box boundaries
 * - Final mockup generation with user parameters
 *
 * NOTE: KHÔNG dùng cho CupDesignPage (dùng cupMockupService.ts)
 */

/**
 * Load image from URL or base64 with detailed error logging
 */
const loadImage = (src: string, imageName: string = 'image'): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      console.log(`✅ [loadImage] Successfully loaded ${imageName}: ${src.substring(0, 100)}...`);
      resolve(img);
    };

    img.onerror = (error) => {
      console.error(`❌ [loadImage] Failed to load ${imageName}`);
      console.error(`   URL: ${src}`);
      console.error(`   Error:`, error);
      reject(new Error(`Failed to load ${imageName}: ${src.substring(0, 100)}...`));
    };

    img.src = src;
  });
};

/**
 * Bounding box interface
 */
export interface BoundingBox {
  x: number;
  y: number;
  width: number;
  height: number;
  area?: number;
  pixelCount?: number;
}

/**
 * Detect red box boundaries from mask image
 * Returns array of bounding boxes (one per red rectangular region)
 *
 * ALGORITHM (Copied EXACTLY from cupMockupService.ts):
 * 1. Scan image pixel by pixel
 * 2. Find red pixels (R > 120 && G < 100 && B < 100)
 * 3. Flood fill to find connected regions
 * 4. Filter by size (count > 200, width > 10, height > 10)
 * 5. Return ALL detected boxes (no filtering to largest)
 *
 * NOTE: This is 100% identical to cupMockupService.ts detectRedBoxes
 * Modified signature: takes HTMLCanvasElement instead of (ctx, width, height)
 */
const detectRedBoxes = (canvas: HTMLCanvasElement): BoundingBox[] => {
  const ctx = canvas.getContext('2d');
  if (!ctx) return [];

  const width = canvas.width;
  const height = canvas.height;
  const imageData = ctx.getImageData(0, 0, width, height);
  const data = imageData.data;
  const visited = new Uint8Array(width * height);
  const boxes: BoundingBox[] = [];

  const isRed = (idx: number) => {
    const r = data[idx];
    const g = data[idx + 1];
    const b = data[idx + 2];
    // STRICT threshold - ô đỏ rõ ràng
    return r > 120 && g < 100 && b < 100;
  };

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const index = (y * width + x);
      const pixelIndex = index * 4;

      if (visited[index]) continue;

      if (isRed(pixelIndex)) {
        let minX = x, maxX = x, minY = y, maxY = y;
        const stack = [index];
        visited[index] = 1;
        let count = 0;

        while (stack.length > 0) {
          const currIdx = stack.pop()!;
          const currX = currIdx % width;
          const currY = Math.floor(currIdx / width);

          count++;
          if (currX < minX) minX = currX;
          if (currX > maxX) maxX = currX;
          if (currY < minY) minY = currY;
          if (currY > maxY) maxY = currY;

          const neighbors = [currIdx - width, currIdx + width, currIdx - 1, currIdx + 1];

          for (const n of neighbors) {
            if (n < 0 || n >= width * height) continue;
            if (Math.abs((n % width) - currX) > 1) continue;

            if (!visited[n] && isRed(n * 4)) {
              visited[n] = 1;
              stack.push(n);
            }
          }
        }

        if (count > 200 && (maxX - minX) > 10 && (maxY - minY) > 10) {
          boxes.push({
            x: minX,
            y: minY,
            width: maxX - minX,
            height: maxY - minY,
            area: (maxX - minX) * (maxY - minY),
            pixelCount: count
          });
        }
      }
    }
  }

  return boxes;
};

/**
 * Generate FINAL mockup with logo at specified position and scale
 *
 * @param cupTemplateUrl - Clean cup template image (no red boxes visible)
 * @param maskImageUrl - Mask image with red box boundaries
 * @param logoImageBase64 - Logo image (already color-transformed by caller)
 * @param position - Logo position { x: -150 to 150, y: -150 to 150 }
 * @param scale - Logo scale (10-100%)
 * @returns Base64 image of cup with logo composite
 */
export const generateFinalMockupWithPosition = async (
  cupTemplateUrl: string,
  maskImageUrl: string,
  logoImageBase64: string,
  position: { x: number; y: number },
  scale: number,
  detailThreshold: number = 100
): Promise<string> => {
  console.log('🎨 [generateFinalMockup] Starting mockup generation');
  console.log('   Cup template URL:', cupTemplateUrl);
  console.log('   Mask URL:', maskImageUrl);
  console.log('   Logo:', logoImageBase64.substring(0, 50) + '...');
  console.log('   Detail threshold:', detailThreshold);

  // 1. Load images with proper error handling
  let cupImg: HTMLImageElement;
  let maskImg: HTMLImageElement;
  let logoImg: HTMLImageElement;

  try {
    [cupImg, maskImg, logoImg] = await Promise.all([
      loadImage(cupTemplateUrl, 'cup template'),
      loadImage(maskImageUrl, 'mask image'),
      loadImage(logoImageBase64, 'logo')
    ]);
  } catch (err) {
    console.error('❌ [generateFinalMockup] Image loading failed:', err);
    throw err;
  }

  // 2. Create canvas with HIGH DPI rendering for sharp output
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Cannot get canvas context');

  // HIGH DPI CANVAS: High-quality final render (consistent with InteractivePreview PIXEL_RATIO)
  // Display size: normalized to 1024px for consistent quality across all templates
  // Pixel size: 1024 × 3 = 3072px (3x DPI for sharp detail preservation)
  const PIXEL_RATIO = 3;  // 3x sharp rendering (same as InteractivePreview)
  const displaySize = 1024;  // Normalized display size for all mockups

  let w = cupImg.width;
  let h = cupImg.height;

  // Scale to normalized display size (1024) while maintaining aspect ratio
  const scaleRatio = Math.min(displaySize / w, displaySize / h);
  w = Math.floor(w * scaleRatio);
  h = Math.floor(h * scaleRatio);

  // Set canvas PIXEL size (display × PIXEL_RATIO)
  canvas.width = w * PIXEL_RATIO;
  canvas.height = h * PIXEL_RATIO;

  // Scale context for smooth rendering (same as InteractivePreview)
  ctx.scale(PIXEL_RATIO, PIXEL_RATIO);

  // 3. Draw cup template (clean, no red boxes)
  // Use logical dimensions (w, h) since ctx.scale() is applied
  ctx.drawImage(cupImg, 0, 0, w, h);

  // 4. Detect red box boundaries from mask image
  // CRITICAL: Mask canvas uses fixed 1024x1024 detection space
  // This MUST match InteractivePreview's DETECTION_SIZE (1024) for red box coordinate consistency
  const maskCanvas = document.createElement('canvas');
  const DETECTION_SIZE = 1024;  // Fixed detection space - MUST match InteractivePreview DETECTION_SIZE
  maskCanvas.width = DETECTION_SIZE;
  maskCanvas.height = DETECTION_SIZE;
  const maskCtx = maskCanvas.getContext('2d');

  if (!maskCtx) {
    console.warn('Cannot get mask context, drawing logo at center');
    // Fallback: draw at center if no mask
    const centerX = w / 2;
    const centerY = h / 2;
    const scaleFactor = scale / 100;
    const logoWidth = logoImg.width * scaleFactor;
    const logoHeight = logoImg.height * scaleFactor;
    const logoX = centerX + position.x - logoWidth / 2;
    const logoY = centerY + position.y - logoHeight / 2;
    ctx.drawImage(logoImg, logoX, logoY, logoWidth, logoHeight);
    return canvas.toDataURL('image/png');
  }

  // Draw mask image scaled to detection space (1024x1024)
  maskCtx.drawImage(maskImg, 0, 0, maskCanvas.width, maskCanvas.height);

  console.log(`📐 [Detection] Mask canvas size: ${maskCanvas.width}x${maskCanvas.height} (fixed detection space)`);
  console.log(`📐 [Detection] Cup display size: ${w}x${h}, pixel size: ${w * PIXEL_RATIO}x${h * PIXEL_RATIO}`);
  console.log(`📐 [Detection] Mask image original: ${maskImg.width}x${maskImg.height}`);
  console.log(`📐 [Detection] Cup image original: ${cupImg.width}x${cupImg.height}`);

  const redBoxes = detectRedBoxes(maskCanvas);

  console.log(`🔍 [Detection] Found ${redBoxes.length} boxes:`, redBoxes);

  // 5. Draw logo for each red box boundary
  // Position and scale are user-controlled, applied relative to each box
  if (redBoxes.length === 0) {
    console.warn('No red boxes detected, drawing logo at center');
    // Fallback: draw at center (use logical dimensions)
    const centerX = w / 2;
    const centerY = h / 2;
    const scaleFactor = scale / 100;
    const logoWidth = logoImg.width * scaleFactor * 0.3; // Default scale
    const logoHeight = logoImg.height * scaleFactor * 0.3;
    const logoX = centerX + position.x - logoWidth / 2;
    const logoY = centerY + position.y - logoHeight / 2;
    ctx.drawImage(logoImg, logoX, logoY, logoWidth, logoHeight);
  } else {
    // Draw one logo per red box
    redBoxes.forEach(box => {
      // Scale factor from detection space (1024x1024) to display space (w×h)
      const scaleX = w / DETECTION_SIZE;   // Conversion from detection→display space
      const scaleY = h / DETECTION_SIZE;   // Actual pixel scaling happens via ctx.scale(PIXEL_RATIO)

      // Calculate logo size based on scale percentage
      // All calculations in detection space (1024x1024 = DETECTION_SIZE)
      const baseWidth = box.width * 0.8; // 80% of box width
      const aspectRatio = logoImg.height / logoImg.width;
      let logoWidth = baseWidth * (scale / 100);
      let logoHeight = logoWidth * aspectRatio;

      // Ensure logo fits in box even after scaling
      if (logoHeight > box.height * 0.8 * (scale / 100)) {
        logoHeight = box.height * 0.8 * (scale / 100);
        logoWidth = logoHeight / aspectRatio;
      }

      // Scale logo from detection space (1024) to display space (w)
      // Actual pixel scaling happens via ctx.scale(PIXEL_RATIO)
      logoWidth = logoWidth * scaleX;
      logoHeight = logoHeight * scaleY;

      // Calculate position relative to box center
      // Box coordinates from detection space (1024) → scale to display space
      const boxCenterX = (box.x + box.width / 2) * scaleX;
      const boxCenterY = (box.y + box.height / 2) * scaleY;

      // Apply position offset (from InteractivePreview)
      const logoX = boxCenterX + (position.x * scaleX) - logoWidth / 2;
      const logoY = boxCenterY + (position.y * scaleY) - logoHeight / 2;

      // Draw logo at scaled size
      ctx.drawImage(logoImg, logoX, logoY, logoWidth, logoHeight);
    });
  }

  // 6. Return final composite as base64
  return canvas.toDataURL('image/png');
};

/**
 * Helper: Get red box boundaries for display purposes
 * Used by InteractivePreview to show constraint zones
 */
export const getRedBoxBoundaries = async (
  maskImageUrl: string,
  targetWidth: number,
  targetHeight: number
): Promise<BoundingBox[]> => {
  const maskImg = await loadImage(maskImageUrl, 'mask image for box detection');

  const canvas = document.createElement('canvas');
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  const ctx = canvas.getContext('2d');

  if (!ctx) return [];

  ctx.drawImage(maskImg, 0, 0, targetWidth, targetHeight);
  const boxes = detectRedBoxes(canvas);

  // Fallback: If no red boxes detected, create a default centered box
  if (boxes.length === 0) {
    console.warn('⚠️ No red boxes detected in mask image, using default centered box');
    const x = Math.floor(targetWidth * 0.25);
    const y = Math.floor(targetHeight * 0.25);
    const width = Math.floor(targetWidth * 0.5);
    const height = Math.floor(targetHeight * 0.5);
    const defaultBox = {
      x,
      y,
      width,
      height,
      area: width * height,
      pixelCount: width * height
    };
    console.log(`Using default box: x=${defaultBox.x}, y=${defaultBox.y}, w=${defaultBox.width}, h=${defaultBox.height}`);
    return [defaultBox];
  }

  return boxes;
};
